import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { appointmentsApi } from "../../../shared/api/appointments";
import { useDebounce } from "../../../shared/hooks/useDebounce";
import { formatDate, formatPhone } from "../../../shared/format";

export interface PatientChoice {
  id: string;
  name: string;
  code: string;
  phone: string | null;
  dob: string | null;
}

interface Props {
  value: PatientChoice | null;
  onChange: (next: PatientChoice | null) => void;
  autoFocus?: boolean;
  error?: string;
  /** When provided, the "Tạo bệnh nhân mới" affordance calls this instead of a
   *  plain link — lets the host preserve its draft + round-trip back (#18561). */
  onCreateNew?: () => void;
}

/**
 * Type-ahead patient picker used by the appointment-create flow and
 * anywhere we need a patient reference. Sources results from
 * `appointmentsApi.searchPatients`. Keyboard-navigable: ↑/↓ to move,
 * Enter to select, Esc to clear menu.
 */
export function PatientCombobox({
  value,
  onChange,
  autoFocus,
  error,
  onCreateNew,
}: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  /** #19820 — nut xoa tra focus ve o nhap, de le tan go tiep duoc ngay. */
  const inputRef = useRef<HTMLInputElement>(null);
  const debounced = useDebounce(q, 200);

  const results = useQuery({
    queryKey: ["appt-patient-search", debounced],
    queryFn: () => appointmentsApi.searchPatients(debounced),
    enabled: open && debounced.length >= 1,
  });

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const items = results.data ?? [];

  function commit(patient: PatientChoice) {
    onChange(patient);
    setQ("");
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(items.length - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (items[active]) commit(items[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  // `grid gap-1.5`, khong phai `space-y-1.5`: <Label> cua Radix render ra <label> von la
  // `display: inline`. Trong luong block, hop dong cua no bi strut line-height cua khoi cha
  // (1.5) keo cao 16px va day xuong 4.5px, nen o nhap tut 10px so voi field ben canh dung
  // `grid gap-1.5` - noi label duoc blockify nen `leading-none` giu dung 14px. Do la #19151:
  // Benh nhan lech Vac xin / Phac do o man Dat lich tiem. Moi field khac da dung `grid gap-1.5`.
  return (
    <div className="relative grid gap-1.5" ref={wrapRef}>
      <Label htmlFor="combo-patient">Bệnh nhân *</Label>
      {value ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-input bg-card px-3 py-2">
          <div className="min-w-0">
            <div className="truncate font-semibold text-foreground">
              {value.name}
            </div>
            <div className="truncate font-mono text-xs text-muted-foreground">
              {value.code}
              {value.phone && ` · ${formatPhone(value.phone)}`}
              {value.dob && ` · ${formatDate(value.dob)}`}
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(null)}
            aria-label="Đổi bệnh nhân khác"
          >
            Đổi
          </Button>
        </div>
      ) : (
        <>
          {/*
            `relative` nay la KHOI NEO cua dropdown, khong chi de dat icon kinh lup (#19820).
            Dropdown phai nam TRONG day va neo bang `top-full`: xem chu thich cua dropdown.
          */}
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              ref={inputRef}
              id="combo-patient"
              /*
               * `text`, khong phai `search` (#19820): Chrome tu ve mot nut ✕ cho `type=search`,
               * nen giu `search` roi them nut xoa cua minh la hai nut canh nhau. Nut native lai
               * khong den duoc bang ban phim va moi trinh duyet ve mot kieu, nen thu bo di la
               * thu khong kiem duoc. Esc da do `onKeyDown` lo.
               */
              type="text"
              value={q}
              placeholder="Tìm theo tên, số ĐT, mã BN…"
              className={`pl-9 pr-9${error ? " border-destructive focus-visible:ring-destructive" : ""}`}
              onFocus={() => setOpen(true)}
              onChange={(e) => {
                setQ(e.target.value);
                setOpen(true);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
              autoFocus={autoFocus}
              aria-expanded={open}
              aria-controls="combo-patient-menu"
              role="combobox"
              aria-autocomplete="list"
            />
            {q && (
              /*
               * Duong thoat hien ra duoc (#19820). Le tan go sai ten thi truoc day phai tai lai
               * ca trang; mot nut xoa noi ro ra la duong ngan nhat, va no khong phu thuoc vao
               * viec dropdown nam dung cho hay khong.
               *
               * `onMouseDown` + `preventDefault` chu khong phai `onClick`: input dang giu focus,
               * nen mot `click` thuong se lam input blur TRUOC khi handler chay. Cung ly do ma
               * cac muc trong danh sach ben duoi dung `onMouseDown`.
               */
              <button
                type="button"
                aria-label="Xoá từ khoá tìm kiếm"
                className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setQ("");
                  setActive(0);
                  setOpen(true);
                  inputRef.current?.focus();
                }}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
            {open && debounced.length >= 1 && (
              /*
               * #19820 — dropdown NEO VAO O NHAP, khong dua vao vi tri tinh.
               *
               * Ban truoc dat `absolute` + `mt-1` ma KHONG co `top`, nen no roi ve vi tri tinh
               * cua chinh no. Trong luong thuong, vi tri tinh la "sau cac phan tu truoc do" —
               * tuc ngay duoi o nhap, dung y. Nhung khoi boc da doi tu `space-y-1.5` sang
               * `grid gap-1.5` o #19151 (de sua le nhan), va trong mot khoi GRID, vi tri tinh
               * cua con abspos la DINH khoi chua. Do duoc trong Chromium, cung markup chi khac
               * khoi boc: grid -> menu top 25 / input top 43 (DE LEN); flow -> menu top 162 /
               * input bottom 158 (nam duoi). Nen dropdown phu len ca nhan lan o nhap, va click
               * vao o thuc ra la click vao dropdown — QA do bang `elementFromPoint` thay DIV
               * chu khong phai INPUT.
               *
               * Nam trong `relative` cua o nhap + `top-full` thi diem neo la DAY O NHAP, khong
               * con phu thuoc vao kieu hien thi cua khoi boc. Doi `grid` ve `space-y` cung se
               * sua duoc trieu chung, nhung lai lam hong dung thu #19151 vua sua.
               */
              <div
                className="absolute left-0 top-full z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-input bg-card p-1 shadow-md"
                id="combo-patient-menu"
                role="listbox"
              >
                {results.isLoading && (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    Đang tìm…
                  </div>
                )}
                {!results.isLoading && items.length === 0 && (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    Không có kết quả.{" "}
                    {onCreateNew ? (
                      <button
                        type="button"
                        className="text-primary underline-offset-4 hover:underline"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          onCreateNew();
                        }}
                      >
                        Tạo bệnh nhân mới
                      </button>
                    ) : (
                      <a
                        href="/patients/new"
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        Tạo bệnh nhân mới
                      </a>
                    )}
                  </div>
                )}
                {items.map((p, i) => (
                  <div
                    key={p.id}
                    role="option"
                    aria-selected={i === active}
                    className={`cursor-pointer rounded-sm px-3 py-2 text-sm ${
                      i === active
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-accent/60"
                    }`}
                    onMouseEnter={() => setActive(i)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      commit(p);
                    }}
                  >
                    <div className="font-medium text-foreground">{p.name}</div>
                    <div className="font-mono text-xs text-muted-foreground">
                      <span>{p.code}</span>
                      {p.phone && <> · {formatPhone(p.phone)}</>}
                      {p.dob && <> · sinh {formatDate(p.dob)}</>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}
